import { get } from 'lodash';
import { useState } from 'react';
import { useReactForm } from '@ninja/utilities';
import {
	CustomerWebSiteAppInitResponse,
	GlobalAPIError,
	MedicalDeclarationQuestionsResponse,
	generateQuote,
	getIsOfflineSubmitted,
	saveMedicalQuestionAnswer,
} from '@shory-health-individual/customer-api';
import { getMemberIndexes } from '../../../common';
import { useAppPlatformContextOptions } from '../../../contexts/app';
import { JourneyDetails } from '../../../server';
import { selectOfflineSubmitted, selectSetIsOfflineSubmitted, useQuoteStore } from '../../../stores';
import { getInitialValues } from './helpers/get-initial-values';
import { IDeclarationQuestionsForm } from './types';
import useDeclarationAnswers from './useDeclarationAnswers';
import useDeclarationValidation from './useDeclarationValidation';

type useDeclarationQuestionsProps = {
	declarationQuestions: MedicalDeclarationQuestionsResponse;
	onSuccess: ({
		quoteReferenceNumber,
		totalAvailablePlans,
	}: {
		quoteReferenceNumber: string;
		totalAvailablePlans: number;
	}) => Promise<void>;
	journeyDetails: JourneyDetails;
	acknowledgementId?: number;
	newBornSettings?: NonNullable<CustomerWebSiteAppInitResponse['systemConfigurationSettings']>['newBornSettings'];
};

export const useDeclarationQuestions = (props: useDeclarationQuestionsProps) => {
	const { declarationQuestions, onSuccess, journeyDetails, acknowledgementId, newBornSettings } = props;

	const { encryptedJourneyId, dependents = [], principalDetails } = journeyDetails;
	const { apiConnectionOptions, onGeneralErrorHandling } = useAppPlatformContextOptions();

	const { getAnswerIdFromSavedAnswer } = useDeclarationAnswers({ declarationQuestions });

	const members = [principalDetails, ...dependents].filter((m) =>
		declarationQuestions.find((member) => member?.memberId === m?.memberId)
	);

	const getMemberQuestions = (memberId: string, parentQuestionId: null | number) => {
		const questions = declarationQuestions.find((member) => member.memberId === memberId)?.questions;

		return questions?.filter((question) => question.parentQuestionId === parentQuestionId) ?? [];
	};

	const { getValidationSchema } = useDeclarationValidation({
		getMemberQuestions,
		declarationQuestions,
		members,
		newBornSettings,
	});

	const isOfflineSubmitted = useQuoteStore(selectOfflineSubmitted);
	const setIsOfflineSubmitted = useQuoteStore(selectSetIsOfflineSubmitted);
	const [isAlertShown, setIsAlertShown] = useState(false);
	const [checkboxStatus, setCheckboxStatus] = useState({ isChecked: false, hasError: false });

	const form = useReactForm<IDeclarationQuestionsForm>({
		initialValues: getInitialValues(journeyDetails, declarationQuestions),
		validationMode: 'zod',
		validation: (formValues) => getValidationSchema(formValues),
		validateBehavior: 'on-submit',
		onFormSubmit: async (formValues) => {
			if (!checkboxStatus.isChecked) {
				return;
			}
			const membersIds = declarationQuestions!.map((member) => member.memberId);
			const requestBody = membersIds.map((memberId) => {
				const questionAnswers = get(formValues, [memberId, 'isMedicalConditionDeclared'])
					? Object.entries(get(formValues, [memberId, 'questions'])).map(([key, value]) => {
							const { answer: formAnswer } = value;
							const questionId = parseInt(key.replace('q-', ''));
							const answerId = getAnswerIdFromSavedAnswer(memberId, questionId, formAnswer) ?? formAnswer;
							const answer = typeof formAnswer === 'string' ? formAnswer : '';

							return {
								questionId,
								answerId,
								answer,
							};
					  })
					: [];

				return {
					memberId,
					height: get(formValues, [memberId, 'height']) ?? 0,
					weight: get(formValues, [memberId, 'weight']) ?? 0,
					isMedicalConditionDeclared: !!get(formValues, [memberId, 'isMedicalConditionDeclared']),
					questionAnswers,
					userAcknowledgementId: acknowledgementId,
				};
			});

			try {
				await saveMedicalQuestionAnswer(apiConnectionOptions)({
					parameters: { journeyId: encryptedJourneyId },
					body: requestBody,
				});
				const generateQuoteResponse = await generateQuote(apiConnectionOptions)({
					parameters: { encryptedQuoteJourneyId: encryptedJourneyId },
					body: undefined,
				});

				if (generateQuoteResponse.totalAvailablePlans < 1) {
					const offlineSubmittedResponse = await getIsOfflineSubmitted(apiConnectionOptions)({
						parameters: { encryptedJourneyId },
					});

					setIsOfflineSubmitted(offlineSubmittedResponse.isSuccess);
				}

				await onSuccess(generateQuoteResponse);

				form.resetForm(formValues);
			} catch (error) {
				await onGeneralErrorHandling(error as GlobalAPIError);
			}
		},
	});

	return {
		form,
		getMemberQuestions,
		checkboxStatus,
		setCheckboxStatus,
		isAlertShown,
		setIsAlertShown,
		members,
		memberIndexes: getMemberIndexes(members, 'relationshipTypeId', 'memberId'),
		isOfflineSubmitted,
		getAnswerIdFromSavedAnswer,
	};
};
